import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Header } from '@/components/Header';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  LayoutDashboard, BookOpen, Settings, ChevronRight, ChevronDown,
  Dumbbell, Factory, Piano, Mic, Calendar, Sparkles, GraduationCap, Gift,
  Clock, CheckCircle, Video, Users, Star, Loader2, Music, Target, Lightbulb,
  BookOpenCheck, MessageCircle
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

const classroomIcons = {
  'music-gym': Dumbbell,
  'music-factory': Factory,
  'modular-piano': Piano,
  'theory-ear-training': Mic,
  'seasonal-genre': Calendar,
  'new-offerings': Sparkles,
  'foundation-lessons': GraduationCap,
  'perks-tiers': Gift,
};

// Detailed classroom literature based on transcripts
const classroomLiterature = {
  'music-gym': {
    tagline: "Build Your Musical Muscles",
    overview: "Think of this as your fitness training for piano. Just like how athletes have specific workouts for strength, speed, and endurance, Music Gym provides systematic exercises that build your technical foundation. This isn't about playing songs—it's about developing the physical and mental capabilities that make everything else easier.",
    whoIsThisFor: [
      "Students who feel their fingers can't keep up with what they hear in their head",
      "Anyone who struggles with fast passages or complex rhythms",
      "Musicians who want to build stamina for longer practice sessions",
      "Those transitioning from casual playing to serious study"
    ],
    keyBenefits: [
      "Develop finger independence so each hand can do its own thing",
      "Build speed without sacrificing accuracy",
      "Create muscle memory that makes difficult passages automatic",
      "Prevent injury through proper technique"
    ],
    talkingPoints: [
      "Ask: 'Do you ever feel like your hands can't keep up with the music you want to play?'",
      "Explain: '3D Training covers three dimensions—technical accuracy, speed development, and musical endurance'",
      "For jazz students: 'JZ Etudes gives you the vocabulary that makes improvisation natural, not forced'",
      "Analogy: 'Athletes don't just play games—they train. This is your training ground.'"
    ]
  },
  'music-factory': {
    tagline: "Where Music Gets Made",
    overview: "Music Factory is where creativity meets craft. This classroom takes you beyond playing notes to actually understanding, creating, and producing music. From transcribing your favorite songs by ear to arranging for different ensembles to recording your own tracks—this is where you become a complete musician, not just a pianist.",
    whoIsThisFor: [
      "Musicians who want to figure out songs without sheet music",
      "Those interested in music production and arrangement",
      "Church musicians who need to create arrangements on the fly",
      "Anyone who wants to understand why their favorite music sounds the way it does"
    ],
    keyBenefits: [
      "Learn to transcribe any song by ear—no more depending on sheet music",
      "Understand how to arrange music for different settings",
      "Get hands-on with music production technology",
      "Develop the skills to create responsive, in-the-moment music"
    ],
    talkingPoints: [
      "Ask: 'Have you ever wanted to just hear a song and be able to play it?'",
      "Explain: 'Transcription is like learning a new language—once you can do it, you can learn anything'",
      "For producers: 'Production class bridges the gap between playing and recording'",
      "For worship leaders: 'Church Hymns & Orchestration helps you lead with confidence'"
    ]
  },
  'modular-piano': {
    tagline: "Your Piano, Your Way",
    overview: "Modular Piano breaks down piano playing into specific, learnable skills. Instead of following a rigid curriculum, you pick the modules that matter to you. Want to accompany yourself while singing? There's a module for that. Want to add fills and ornaments like the pros? We've got you covered. This is customized piano education.",
    whoIsThisFor: [
      "Singer-songwriters who want to accompany themselves",
      "Keyboardists who play in bands or worship teams",
      "Those who want to learn specific techniques without a full course",
      "Players who want to add professional polish to their playing"
    ],
    keyBenefits: [
      "Learn exactly what you need, skip what you don't",
      "Master different keyboard sounds—EP, organ, synth, clav",
      "Add professional ornaments, fills, and dynamics",
      "Build independence between hands for fuller sound"
    ],
    talkingPoints: [
      "Ask: 'What's the one thing you wish you could do better on piano?'",
      "Explain: 'Each module is self-contained—you can take just what you need'",
      "For singers: 'Sing & Play teaches you to simplify accompaniment while you perform'",
      "For band players: 'Keyboard Styles covers EP, clav, organ—sounds that define modern music'"
    ]
  },
  'theory-ear-training': {
    tagline: "Understand What You Play",
    overview: "Theory & Ear Training is your musicianship foundation. This isn't dry textbook theory—it's practical knowledge that changes how you hear and create music. Learn to read chord charts, recognize intervals by ear, understand why certain progressions work, and communicate with other musicians in their language.",
    whoIsThisFor: [
      "Self-taught musicians who want to fill in the gaps",
      "Those who can play but don't understand what they're playing",
      "Musicians who want to jam confidently with others",
      "Anyone preparing for music exams or professional work"
    ],
    keyBenefits: [
      "Hear a note or chord and know what it is instantly",
      "Read jazz chord charts and lead sheets",
      "Understand harmonic progressions and substitutions",
      "Communicate effectively with other musicians"
    ],
    talkingPoints: [
      "Ask: 'Can you hear a melody and know what notes they are?'",
      "Explain: 'Ear training is like learning to see—once you can hear it, music opens up'",
      "For jammers: 'Jazz Chord Charts class lets you walk into any session and play'",
      "Reality check: 'Most professional musicians have strong theory—it's the language of music'"
    ]
  },
  'seasonal-genre': {
    tagline: "Play What the World Plays",
    overview: "Seasonal & Genre Based is your ticket to being the musician everyone wants at gatherings. From Christmas carols to Bollywood hits, from blues to Latin rhythms—this classroom keeps you relevant and versatile. Learn the authentic techniques that make each genre sound right, not like a generic cover.",
    whoIsThisFor: [
      "Musicians who play for family gatherings and events",
      "Those who want to expand beyond one style",
      "Players who want to stay current with trending music",
      "Anyone interested in world music and diverse styles"
    ],
    keyBenefits: [
      "Be ready for any occasion—holidays, parties, cultural events",
      "Learn authentic techniques for each genre",
      "Stay current with trending songs and viral hits",
      "Develop genuine versatility, not surface-level familiarity"
    ],
    talkingPoints: [
      "Ask: 'What kind of music do people usually request from you?'",
      "Explain: 'Each genre has its own feel—we teach you the authentic techniques'",
      "For Indian students: 'Bollywood and Coke Studio modules are very popular'",
      "Seasonal tip: 'Christmas module is perfect for December gigs and family gatherings'"
    ]
  },
  'new-offerings': {
    tagline: "Learn Together, Grow Together",
    overview: "New Offerings is where individual practice meets group experience. These aren't traditional lessons—they're collaborative sessions where you play with other musicians, perform in workshops, and learn real-world skills that only come from doing. This is the closest thing to being in a band while you're still learning.",
    whoIsThisFor: [
      "Musicians who practice alone but want to play with others",
      "Those preparing for live performance",
      "Anyone who wants to develop their musical ear through collaboration",
      "Students interested in the business side of music"
    ],
    keyBenefits: [
      "Experience playing with other musicians in real-time",
      "Get performance experience in a supportive environment",
      "Learn the practical skills working musicians need",
      "Build confidence through group activities"
    ],
    talkingPoints: [
      "Ask: 'Have you ever played music with other people?'",
      "Explain: 'Jam sessions teach you to listen and respond—skills you can't learn alone'",
      "For aspiring pros: 'Working Musician Skills covers performing, recording, and teaching'",
      "Unique offering: 'Riff with Jason Zac is mentorship with a professional artist'"
    ]
  },
  'foundation-lessons': {
    tagline: "Build It Right From The Start",
    overview: "Foundation Lessons is where basics are done right. Whether you're starting fresh or rebuilding shaky fundamentals, this classroom ensures you have the proper technique, reading ability, and musical understanding that everything else builds on. Skip this at your peril—bad habits are hard to break later.",
    whoIsThisFor: [
      "Complete beginners starting their musical journey",
      "Self-taught players who developed bad habits",
      "Those returning to piano after a long break",
      "Anyone who feels their basics aren't solid"
    ],
    keyBenefits: [
      "Establish correct technique from day one",
      "Learn to read music properly",
      "Understand fundamental theory concepts",
      "Build a foundation that supports advanced study"
    ],
    talkingPoints: [
      "Ask: 'How did you first learn piano? Did you have formal training?'",
      "Explain: 'Proper foundation prevents injuries and makes advanced stuff easier'",
      "For returners: 'Think of this as a reset—we'll identify and fix any bad habits'",
      "Important: 'Everyone thinks they can skip basics. The pros never did.'"
    ]
  }
};

const AdminSidebar = () => {
  return (
    <aside className="fixed left-0 top-0 h-screen w-64 admin-sidebar border-r border-[#2A2A2A] pt-20 hidden lg:block">
      <nav className="p-4 space-y-2">
        <Link to="/admin" className="flex items-center gap-3 px-4 py-3 rounded-lg text-[#A1A1AA] hover:bg-white/5 hover:text-[#F5F5DC] transition-colors">
          <LayoutDashboard size={20} />
          <span>Dashboard</span>
        </Link>
        <Link to="/admin/courses" className="flex items-center gap-3 px-4 py-3 rounded-lg bg-[#D4AF37]/10 text-[#D4AF37]">
          <BookOpen size={20} />
          <span>Course Guide</span>
        </Link>
        <Link to="/admin/settings" className="flex items-center gap-3 px-4 py-3 rounded-lg text-[#A1A1AA] hover:bg-white/5 hover:text-[#F5F5DC] transition-colors">
          <Settings size={20} />
          <span>Class Settings</span>
        </Link>
        <Link to="/admin/perks" className="flex items-center gap-3 px-4 py-3 rounded-lg text-[#A1A1AA] hover:bg-white/5 hover:text-[#F5F5DC] transition-colors">
          <Gift size={20} />
          <span>Perks & Tiers</span>
        </Link>
        <Link to="/admin/training" className="flex items-center gap-3 px-4 py-3 rounded-lg text-[#A1A1AA] hover:bg-white/5 hover:text-[#F5F5DC] transition-colors">
          <GraduationCap size={20} />
          <span>Staff Training</span>
        </Link>
      </nav>
    </aside>
  );
};

export const AdminCourseGuide = () => {
  const [classrooms, setClassrooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedClassroom, setSelectedClassroom] = useState(null);

  useEffect(() => {
    fetchClassrooms();
  }, []);

  const fetchClassrooms = async () => {
    try {
      const response = await axios.get(`${API}/classrooms`);
      // Filter out perks-tiers
      setClassrooms(response.data.filter(c => c.id !== 'perks-tiers'));
    } catch (error) {
      console.error('Error fetching classrooms:', error);
      toast.error('Failed to load course data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <Loader2 className="animate-spin text-[#D4AF37]" size={40} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505]">
      <Header variant="solid" />
      <AdminSidebar />
      
      <main className="lg:ml-64 pt-20 p-6">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <h1 className="font-heading text-3xl text-[#EDEDED] mb-2">Course Guide</h1>
            <p className="text-[#A1A1AA]">
              Your complete reference for understanding and explaining each classroom to students
            </p>
          </div>

          {/* Quick Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-[#121212] border border-[#2A2A2A] rounded-xl p-4 text-center">
              <p className="font-heading text-3xl text-[#D4AF37]">{classrooms.length}</p>
              <p className="text-[#A1A1AA] text-sm">Classrooms</p>
            </div>
            <div className="bg-[#121212] border border-[#2A2A2A] rounded-xl p-4 text-center">
              <p className="font-heading text-3xl text-[#D4AF37]">
                {classrooms.reduce((acc, c) => acc + (c.classes?.length || 0), 0)}
              </p>
              <p className="text-[#A1A1AA] text-sm">Total Classes</p>
            </div>
            <div className="bg-[#121212] border border-[#2A2A2A] rounded-xl p-4 text-center">
              <p className="font-heading text-3xl text-[#D4AF37]">30-90</p>
              <p className="text-[#A1A1AA] text-sm">Min Duration</p>
            </div>
            <div className="bg-[#121212] border border-[#2A2A2A] rounded-xl p-4 text-center">
              <p className="font-heading text-3xl text-[#D4AF37]">HD</p>
              <p className="text-[#A1A1AA] text-sm">Recordings</p>
            </div>
          </div>

          {/* Classroom List */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
            {classrooms.map((classroom) => {
              const IconComponent = classroomIcons[classroom.id] || Music;
              const literature = classroomLiterature[classroom.id];
              
              return (
                <button
                  key={classroom.id}
                  onClick={() => setSelectedClassroom(classroom)}
                  className={`text-left p-6 rounded-xl border transition-all ${
                    selectedClassroom?.id === classroom.id
                      ? 'bg-[#D4AF37]/10 border-[#D4AF37]'
                      : 'bg-[#121212] border-[#2A2A2A] hover:border-[#D4AF37]/50'
                  }`}
                  data-testid={`select-classroom-${classroom.id}`}
                >
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-12 h-12 rounded-xl bg-[#D4AF37]/10 flex items-center justify-center">
                      <IconComponent className="text-[#D4AF37]" size={24} />
                    </div>
                    <div>
                      <h3 className="font-heading text-lg text-[#F5F5DC]">{classroom.name}</h3>
                      <p className="text-xs text-[#A1A1AA]">{classroom.classes?.length || 0} classes</p>
                    </div>
                  </div>
                  {literature && (
                    <p className="text-[#D4AF37] text-sm italic">"{literature.tagline}"</p>
                  )}
                </button>
              );
            })}
          </div>

          {/* Selected Classroom Detail */}
          {selectedClassroom && (
            <div className="bg-[#121212] border border-[#2A2A2A] rounded-2xl overflow-hidden">
              {(() => {
                const IconComponent = classroomIcons[selectedClassroom.id] || Music;
                const literature = classroomLiterature[selectedClassroom.id];
                
                return (
                  <>
                    {/* Header */}
                    <div className="p-6 border-b border-[#2A2A2A] bg-gradient-to-r from-[#D4AF37]/10 to-transparent">
                      <div className="flex items-center gap-4">
                        <div className="w-16 h-16 rounded-xl bg-[#D4AF37]/20 flex items-center justify-center">
                          <IconComponent className="text-[#D4AF37]" size={32} />
                        </div>
                        <div>
                          <h2 className="font-heading text-2xl text-[#F5F5DC]">{selectedClassroom.name}</h2>
                          {literature && (
                            <p className="text-[#D4AF37] italic">"{literature.tagline}"</p>
                          )}
                        </div>
                      </div>
                    </div>

                    <Tabs defaultValue="overview" className="p-6">
                      <TabsList className="bg-black/30 border border-[#2A2A2A] mb-6">
                        <TabsTrigger value="overview" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black">
                          Overview
                        </TabsTrigger>
                        <TabsTrigger value="talking-points" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black">
                          Talking Points
                        </TabsTrigger>
                        <TabsTrigger value="classes" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black">
                          Classes ({selectedClassroom.classes?.length || 0})
                        </TabsTrigger>
                      </TabsList>

                      {/* Overview Tab */}
                      <TabsContent value="overview" className="space-y-6">
                        {literature ? (
                          <>
                            <div>
                              <h3 className="font-heading text-lg text-[#D4AF37] mb-3 flex items-center gap-2">
                                <BookOpenCheck size={20} />
                                What Is This Classroom?
                              </h3>
                              <p className="text-[#A1A1AA] leading-relaxed">{literature.overview}</p>
                            </div>

                            <div className="grid md:grid-cols-2 gap-6">
                              <div>
                                <h3 className="font-heading text-lg text-[#D4AF37] mb-3 flex items-center gap-2">
                                  <Target size={20} />
                                  Who Is This For?
                                </h3>
                                <ul className="space-y-2">
                                  {literature.whoIsThisFor.map((item, idx) => (
                                    <li key={idx} className="flex items-start gap-2 text-[#EDEDED]">
                                      <CheckCircle size={16} className="text-green-400 mt-1 flex-shrink-0" />
                                      <span>{item}</span>
                                    </li>
                                  ))}
                                </ul>
                              </div>

                              <div>
                                <h3 className="font-heading text-lg text-[#D4AF37] mb-3 flex items-center gap-2">
                                  <Star size={20} />
                                  Key Benefits
                                </h3>
                                <ul className="space-y-2">
                                  {literature.keyBenefits.map((item, idx) => (
                                    <li key={idx} className="flex items-start gap-2 text-[#EDEDED]">
                                      <Star size={16} className="text-[#D4AF37] mt-1 flex-shrink-0" />
                                      <span>{item}</span>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            </div>
                          </>
                        ) : (
                          <p className="text-[#A1A1AA]">{selectedClassroom.description}</p>
                        )}
                      </TabsContent>

                      {/* Talking Points Tab */}
                      <TabsContent value="talking-points" className="space-y-6">
                        {literature?.talkingPoints ? (
                          <div>
                            <h3 className="font-heading text-lg text-[#D4AF37] mb-4 flex items-center gap-2">
                              <MessageCircle size={20} />
                              Use These During Student Calls
                            </h3>
                            <div className="space-y-4">
                              {literature.talkingPoints.map((point, idx) => (
                                <div key={idx} className="p-4 rounded-lg bg-black/30 border border-[#2A2A2A]">
                                  <p className="text-[#EDEDED]">{point}</p>
                                </div>
                              ))}
                            </div>
                          </div>
                        ) : (
                          <p className="text-[#A1A1AA]">No talking points available for this classroom.</p>
                        )}
                      </TabsContent>

                      {/* Classes Tab */}
                      <TabsContent value="classes" className="space-y-4">
                        {selectedClassroom.classes?.map((cls) => (
                          <div key={cls.id} className="p-4 rounded-lg border border-[#2A2A2A] bg-black/20">
                            <div className="flex items-start justify-between gap-4">
                              <div className="flex-1">
                                <h4 className="text-[#F5F5DC] font-medium mb-1">{cls.name}</h4>
                                <p className="text-[#A1A1AA] text-sm mb-3">{cls.description}</p>
                                
                                <div className="flex flex-wrap gap-4 text-xs">
                                  <span className="flex items-center gap-1 text-[#A1A1AA]">
                                    <Clock size={12} className="text-[#D4AF37]" />
                                    {cls.duration_minutes || 60} min
                                  </span>
                                  <Badge 
                                    variant="outline" 
                                    className={cls.class_type === 'compulsory' 
                                      ? 'border-red-500/50 text-red-400' 
                                      : 'border-[#D4AF37]/50 text-[#D4AF37]'
                                    }
                                  >
                                    {cls.class_type || 'elective'}
                                  </Badge>
                                  <span className="flex items-center gap-1 text-[#A1A1AA]">
                                    <Video size={12} className="text-[#D4AF37]" />
                                    {cls.delivery_mode || 'both'}
                                  </span>
                                </div>
                              </div>
                            </div>
                            
                            {(cls.usps?.length > 0) && (
                              <div className="mt-3 pt-3 border-t border-[#2A2A2A]">
                                <div className="flex flex-wrap gap-2">
                                  {cls.usps.map((usp, idx) => (
                                    <span key={idx} className="px-2 py-1 rounded-full bg-[#D4AF37]/10 text-[#D4AF37] text-xs">
                                      {usp}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                      </TabsContent>
                    </Tabs>
                  </>
                );
              })()}
            </div>
          )}

          {!selectedClassroom && (
            <div className="text-center py-12 text-[#A1A1AA]">
              <BookOpen size={40} className="mx-auto mb-4 opacity-50" />
              <p>Select a classroom above to see detailed information</p>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default AdminCourseGuide;
